import { FetchError } from '@atlassian/jira-fetch';
import type {
	ThirdPartyEntity,
	ThirdPartyFolderAndSubfolders,
	ThirdPartySubfolders,
} from '../../types';

interface HandleAuthenticationFactoryParams {
	retries: number;
	requestAuthenticationToken: () => Promise<boolean>;
}

// Currently each request creates a new ThirdPartyClient, so this needs to be a global variable.
// TODO: We should fix this to make each third party use the same ThirdPartyClient https://hello.atlassian.net/browse/GUN-2124
let multiplesAuthenticationProps = {
	isRequestingAuthentication: false,
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	requestFailedByAuthentication: [] as Array<() => Promise<any>>,
};

export const resetMultiplesAuthenticationProps = () => {
	multiplesAuthenticationProps = {
		isRequestingAuthentication: false,
		requestFailedByAuthentication: [],
	};
};

export const handleAuthenticationFactory =
	({ retries, requestAuthenticationToken }: HandleAuthenticationFactoryParams) =>
	async <T,>(doRequest: () => Promise<T>): Promise<T> => {
		if (retries < 0) {
			throw new Error('Too many authentication retries');
		}

		let response: T;
		try {
			response = await doRequest();
		} catch (error) {
			if (
				error instanceof FetchError &&
				error.statusCode === 400 &&
				error.originalResponse &&
				(await error.originalResponse.json()).errorCode === 'INVALID_AUTH_TOKEN'
			) {
				let resolve: (a: T) => void;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				let reject: (reason?: any) => void;
				const promise = new Promise<T>((r, rj) => {
					resolve = r;
					reject = rj;
				});

				multiplesAuthenticationProps.requestFailedByAuthentication.push(() =>
					doRequest().then(resolve, reject),
				);
				if (!multiplesAuthenticationProps.isRequestingAuthentication) {
					multiplesAuthenticationProps.isRequestingAuthentication = true;
					const done = await requestAuthenticationToken();
					if (!done) {
						resetMultiplesAuthenticationProps();
						throw new Error('Authentication cancelled');
					}
					multiplesAuthenticationProps.isRequestingAuthentication = false;
					const factory = handleAuthenticationFactory({
						retries: retries - 1,
						requestAuthenticationToken,
					});

					await Promise.allSettled(
						multiplesAuthenticationProps.requestFailedByAuthentication.map((doRequestAgain) =>
							factory(doRequestAgain).catch(reject),
						),
					);
					multiplesAuthenticationProps.requestFailedByAuthentication = [];
				}
				return promise;
			}
			throw error;
		}

		return response;
	};

type ThirdPartyFolderData = ThirdPartyEntity & {
	subfolders?: ThirdPartyEntity[];
};

export const transformToFolderAndSubfolderState = (
	data: ThirdPartyFolderData[],
): ThirdPartyFolderAndSubfolders => {
	const subfolders: ThirdPartySubfolders = {};
	const folders = data.map((folder) => {
		if (folder.subfolders && folder.subfolders.length > 0) {
			subfolders[folder.id] = folder.subfolders;
		}
		return {
			id: folder.id,
			name: folder.name,
		};
	});
	return {
		folders,
		subfolders,
	};
};
